<script setup lang="ts">
/**
 * Toast Component
 *
 * Presentational toast with two usage modes:
 *  - Global host (App.vue): driven by the central `useToast` service, which owns
 *    dismiss timing. Mounted with `:duration="0"` to disable this component's own
 *    timer so the service is the single source of truth.
 *  - Legacy direct callers (`v-model:show` / `@close`): the component self-dismisses
 *    after `duration` ms and emits `update:show` + `close` so callers reset their
 *    own state, preserving the original contract.
 *
 * Click the toast to dismiss it early.
 */
import { computed, watch, onBeforeUnmount } from 'vue'
import { CheckCircle2, XCircle, AlertTriangle, Info } from 'lucide-vue-next'
import type { ToastVariant } from '@/composables/useToast'

interface ToastProps {
  show: boolean
  message?: string
  variant?: ToastVariant
  /** Auto-dismiss delay (ms) for legacy callers. 0 disables the internal timer. */
  duration?: number
}

const props = withDefaults(defineProps<ToastProps>(), {
  message: 'Changes saved',
  variant: 'success',
  duration: 2000,
})

const emit = defineEmits<{
  close: []
  'update:show': [value: boolean]
}>()

const ICONS: Record<ToastVariant, typeof CheckCircle2> = {
  success: CheckCircle2,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
}

// Errors and warnings are urgent — announce assertively. Success/info are polite.
const isUrgent = computed(() => props.variant === 'error' || props.variant === 'warning')
const icon = computed(() => ICONS[props.variant])

let timer: ReturnType<typeof setTimeout> | undefined
function clearTimer(): void {
  if (timer) {
    clearTimeout(timer)
    timer = undefined
  }
}

function dismiss(): void {
  clearTimer()
  emit('update:show', false)
  emit('close')
}

// Legacy self-dismiss: when shown with a positive duration, auto-hide and notify
// the caller. The global host passes duration=0, leaving timing to `useToast`.
watch(
  () => props.show,
  shown => {
    clearTimer()
    if (shown && props.duration > 0) {
      timer = setTimeout(dismiss, props.duration)
    }
  },
  { immediate: true }
)

onBeforeUnmount(clearTimer)
</script>

<template>
  <Transition
    enter-active-class="transition-all duration-300 ease-out"
    enter-from-class="opacity-0 translate-y-4"
    enter-to-class="opacity-100 translate-y-0"
    leave-active-class="transition-all duration-200 ease-in"
    leave-from-class="opacity-100 translate-y-0"
    leave-to-class="opacity-0 translate-y-4"
  >
    <div v-if="show" class="toast-wrapper">
      <div
        class="toast-box"
        :class="`toast-box--${variant}`"
        :role="isUrgent ? 'alert' : 'status'"
        :aria-live="isUrgent ? 'assertive' : 'polite'"
        tabindex="0"
        aria-label="Dismiss notification"
        title="Dismiss"
        @click="dismiss"
        @keydown.enter="dismiss"
        @keydown.space.prevent="dismiss"
      >
        <component :is="icon" class="toast-box__icon w-4 h-4 flex-shrink-0" />
        <span class="text-sm font-medium">{{ message }}</span>
      </div>
    </div>
  </Transition>
</template>
